/**
 * Reports > Dashboards index (M27 PR 3) — /reports/dashboards.
 *
 * Linking surface for the 4 pre-built M21 dashboards. Each card
 * shows a quick-summary number (live, from the existing tables)
 * and the most-recent underlying data timestamp so HR can tell at
 * a glance whether a dashboard's data has stagnated.
 *
 * The 4 dashboards live at their existing M21 URLs
 * (/reports/{headcount,turnover,compensation,compliance}); this
 * index sits at /reports/dashboards as the canonical entry point.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BarChart3,
  DollarSign,
  ShieldCheck,
  TrendingDown,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import type { DashboardSummary } from "../../lib/api";

const _ICONS: Record<DashboardSummary["key"], LucideIcon> = {
  headcount: Users,
  turnover: TrendingDown,
  compensation: DollarSign,
  compliance: ShieldCheck,
};

const _DESCRIPTION_KEYS: Record<DashboardSummary["key"], string> = {
  headcount: "policies.rep_dash_desc_headcount",
  turnover: "policies.rep_dash_desc_turnover",
  compensation: "policies.rep_dash_desc_compensation",
  compliance: "policies.rep_dash_desc_compliance",
};

export function DashboardsIndexPage() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["dashboards-index"],
    queryFn: () => api.getDashboardsIndex(),
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const dashboards = q.data?.dashboards ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.rep_dashboards_title")}
        subtitle={t("policies.rep_dashboards_subtitle")}
        breadcrumbs={[
          { label: t("policies.rep_breadcrumb"), href: "/reports" },
          { label: t("policies.rep_dashboards_breadcrumb") },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dashboards.map((d) => (
          <DashboardCard key={d.key} dashboard={d} />
        ))}
      </div>

      <p className="text-xs text-text-tertiary">
        <BarChart3 size={12} className="me-1 inline" aria-hidden />
        {t("policies.rep_dash_need_else")}
        <Link to="/reports/custom-builder" className="text-brand hover:underline">
          {t("policies.rep_dash_custom_builder")}
        </Link>
        {t("policies.rep_dash_for_today")}
      </p>
    </div>
  );
}

function DashboardCard({ dashboard }: { dashboard: DashboardSummary }) {
  const { t } = useTranslation();
  const Icon = _ICONS[dashboard.key];
  const description = t(_DESCRIPTION_KEYS[dashboard.key]);
  const refreshedText = dashboard.last_refreshed
    ? t("policies.rep_dash_last_data", { date: new Date(dashboard.last_refreshed).toLocaleDateString() })
    : t("policies.rep_dash_no_data");

  return (
    <Link
      to={dashboard.link_to}
      className="group relative overflow-hidden rounded-xl border border-border-subtle bg-surface-default p-4 transition-shadow hover:shadow-sm"
    >
      {/* 2px brand-gradient strip — same signature as Mission Control KPI cards. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
      />
      <header className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-ai-bg text-ai">
          <Icon size={16} aria-hidden />
        </span>
        <h2 className="text-sm font-semibold text-text-primary">
          {dashboard.label}
        </h2>
        <ArrowRight
          size={14}
          aria-hidden
          className="ms-auto text-text-tertiary transition-transform group-hover:translate-x-0.5"
        />
      </header>
      <p className="mt-2 text-xs text-text-secondary">{description}</p>
      <p className="mt-3 text-base font-semibold text-text-primary">
        {dashboard.quick_summary}
      </p>
      <p className="mt-1 text-xs text-text-tertiary">{refreshedText}</p>
    </Link>
  );
}
